import React, { useCallback, useEffect, useRef, useState } from "react";
import { Download, FileCheck2, Upload, Loader2, FileDown } from "lucide-react";
import { Tile } from "../../Primitives";
import ReasonModal from "./ReasonModal";
import ImportReport from "./ImportReport";
import { relTime, InlineMsg } from "../../apikeys/shared";
import { actorName } from "../../modules/util";
import { OriginBadge } from "../../../lib/origin.jsx";
import { getInstallSummary, validateInstallBase, uploadInstallBase, downloadUpload, downloadFile } from "../../../lib/classificationApi";
import { formatNumber } from "../../../lib/utils";

const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm transition-colors disabled:opacity-40";

function Stat({ label, value, sub, origin, testid }) {
  return (
    <div className="px-4 py-3" data-testid={testid}>
      <div className="kpi-label flex items-center gap-2 text-[10px]">{label}{origin && <OriginBadge origin={origin} source={`Install base · ${label}`} className="ml-auto" testid={`${testid}-origin`} />}</div>
      <div className="kpi-value text-xl mt-1">{value}</div>
      {sub && <div className="text-[10.5px] text-muted font-mono-tabular">{sub}</div>}
    </div>
  );
}

function Uploads({ items, canEdit, actor, onError }) {
  if (!items.length) return <div className="px-4 py-4 text-[11.5px] text-muted" data-testid="install-uploads-empty">No uploads yet — every area uses Synthetic catchment estimates.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11.5px]">
        <thead><tr className="text-[10px] uppercase tracking-widest text-subtle border-b border-border">
          {["When", "File", "Rows", "Areas", "Reason", "By", ""].map((h, i) => <th key={i} className="text-left font-semibold px-4 py-2">{h}</th>)}
        </tr></thead>
        <tbody>
          {items.map(u => (
            <tr key={u.id} data-testid={`install-upload-${u.id}`} className="border-b border-border/60 hover:bg-surface-2/40">
              <td className="px-4 py-2 font-mono-tabular text-muted whitespace-nowrap">{relTime(u.ts)}</td>
              <td className="px-4 py-2 font-mono-tabular">{u.original_filename}</td>
              <td className="px-4 py-2 font-mono-tabular">{u.accepted} ok · {u.rejected_count} rejected</td>
              <td className="px-4 py-2 font-mono-tabular">{u.areas_replaced}</td>
              <td className="px-4 py-2 text-muted min-w-[200px]">{u.reason}</td>
              <td className="px-4 py-2 whitespace-nowrap">{actorName(u.uploaded_by)}</td>
              <td className="px-4 py-2 text-right">
                {canEdit && <button data-testid={`install-upload-download-${u.id}`} className={btn} onClick={() => downloadUpload(u, actor).catch(e => onError(e.message))}><FileDown size={11} /> Original</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Install base: Synthetic catchment estimates, CSV template, validation report and Actual uploads.
export default function InstallBasePanel({ canEdit, actor, onChanged }) {
  const [sum, setSum] = useState(null);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const load = useCallback(() => getInstallSummary().then(setSum).catch(e => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);

  async function validate(f) {
    setFile(f); setReport(null); setError(null); setBusy(true);
    try { setReport(await validateInstallBase(f, actor)); } catch (e) { setError(e.message); }
    setBusy(false);
  }

  return (
    <Tile dense testid="install-base-panel" title="Equipment install base"
          action={<button data-testid="install-template" className={btn} onClick={() => downloadFile("/install-base/template", "install-base-template.csv")}><Download size={11} /> CSV template</button>}>
      {sum && (
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border border-b border-border">
          <Stat testid="install-stat-synthetic" label="Synthetic estimates" origin="synthetic" value={formatNumber(sum.synthetic.units)} sub={`${formatNumber(sum.synthetic.rows)} model × area rows`} />
          <Stat testid="install-stat-uploaded" label="Uploaded (Actual)" origin={sum.uploaded.rows ? "actual" : undefined} value={formatNumber(sum.uploaded.units)} sub={`${formatNumber(sum.uploaded.rows)} rows`} />
          <Stat testid="install-stat-areas" label="Areas with uploaded data" value={`${sum.areas_uploaded} / ${sum.areas_total}`} sub="city / municipality catchments" />
        </div>
      )}
      <div className="px-4 py-3 space-y-3 border-b border-border">
        <div className="text-[11px] text-muted leading-snug">
          Each valid row replaces the Synthetic estimate for that model in that city/municipality and is labelled Actual. Columns: model, city_municipality, province, region, units_in_operation, optional compatible_skus (separated by “;”).
        </div>
        {canEdit ? (
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="install-choose-file" className={btn} disabled={busy} onClick={() => fileRef.current?.click()}>
              {busy ? <Loader2 size={11} className="animate-spin" /> : <FileCheck2 size={11} />} {file ? "Choose another file" : "Choose CSV & validate"}
            </button>
            <input ref={fileRef} data-testid="install-file" type="file" accept=".csv,text/csv" className="hidden"
                   onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) validate(f); }} />
            {file && <span className="text-[11px] font-mono-tabular text-muted" data-testid="install-file-name">{file.name}</span>}
            {report && !report.applied && report.accepted > 0 && (
              <button data-testid="install-apply" onClick={() => setConfirm(true)}
                      className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold bg-primary text-primary-fg rounded-sm hover:opacity-90">
                <Upload size={11} /> Apply {formatNumber(report.accepted)} valid rows
              </button>
            )}
          </div>
        ) : <div className="text-[11px] text-subtle" data-testid="install-readonly">Read-only · only the IT Head or COO can upload install base data.</div>}
        {error && <InlineMsg testid="install-error">{error}</InlineMsg>}
        {report && (
          <>
            <ImportReport report={report} kind="install" />
            {!report.applied && report.accepted > 0 && (
              <div className="text-[11px] text-muted" data-testid="install-preview">
                Will replace {formatNumber(report.rows_replaced)} existing rows across {report.areas.length} areas · {formatNumber(report.units)} units · models {report.models.join(", ")}.
              </div>
            )}
          </>
        )}
      </div>
      {sum && <Uploads items={sum.uploads} canEdit={canEdit} actor={actor} onError={setError} />}
      {confirm && (
        <ReasonModal title="Apply install base upload" testid="install-confirm" confirmLabel="Apply upload" onClose={() => setConfirm(false)}
                     impact={<div>{formatNumber(report.accepted)} rows from <span className="font-mono-tabular">{file.name}</span> replace the matching estimates in {report.areas.length} areas and become Actual. The original file is archived in object storage.</div>}
                     onConfirm={async (reason) => {
                       const res = await uploadInstallBase(file, reason, actor);
                       setReport(res); load();
                       onChanged(res, `Install base upload applied · ${res.accepted} rows`);
                     }} />
      )}
    </Tile>
  );
}
